import _get from 'lodash/get';
import _assign from 'lodash/assign';
import PlayerType from '../enums/player-type';
import MediaItem from '../model/media-item';

/**
 *
 * @module PlayerTypeConfig
 * @class PlayerTypeConfig
 * @desc Base PlayerTypeConfig. The Player type configuration object is passed to each player
 * and is used to determine the type of player to create.
 *
 */
class PlayerTypeConfig {

    /**
     *
     * @param {JSON} data The JSON object used to create the PlayerTypeConfig
     */
    constructor(config = {}) {

        /**
         *
         * @type {Object}
         * @desc The default configuration for the player.
         *
         */
        const defaultPlayerConfig = {
            domTarget: 'media-player',
            playerType: PlayerType.HTML5,
            controls: true,
            autoplay: true,
            playsinline: true,
            crossorigin: 'anonymous',
            width: undefined,
            height: undefined,
            loop: false,
            mediaItem: {
                src: undefined,
                type: undefined,
                poster: undefined,
            },
            mute: false,
            preload: 'metadata',
            deviceInfo: {
                userAgent: undefined,
                is: {
                    ios: undefined,
                    blackberry: undefined,
                    android: undefined,
                    windowsphone: undefined,
                    windows: undefined,
                    linux: undefined,
                    macintosh: undefined,
                    webkit: undefined,
                    gecko: undefined,
                    trident: undefined,
                    chrome: undefined,
                    firefox: undefined,
                    opera: undefined,
                    operamini: undefined,
                    msie: undefined,
                    silk: undefined,
                    safari: undefined,
                    tablet: undefined,
                    phone: undefined,
                    desktop: undefined,
                },
                platform: undefined,
                platformVersion: undefined,
                browser: undefined,
                browserVersion: undefined,
                formFactor: undefined,
            },
        };

        /**
         *
         * @type {PlayerTypeConfig} Object player type config. This config will specify player specific configuration.
         * e.g. For bam-video-players, we'll need to pass on the SDK config. This is where that
         * config would be attached.
         *
         */
        this.config = _assign(defaultPlayerConfig, _get(config, 'playerTypeConfig', {}));

        /**
         *
         * @type {MediaItem}
         * @desc The initial MediaItem. A MediaItem represents media for playback as well as associated
         * objects such as vtt for subtitles.
         *
         */
        this._mediaItem = new MediaItem(_get(this.config, 'mediaItem'));

        /**
         *
         * @type {PlayerType}
         * @desc The type of player to create. Currently this is not used. We only have 1 player "HTML5".
         * The thought behind this property is to provide a way for the consumer to specify a player to create.
         *
         */
        this.playerType = _get(this.config, 'playerType', PlayerType.HTML5);

        /**
         *
         * @type {String} the id of the parent element for the player
         * @desc The id of the target parent element for the player. The generated `<video/>`
         * element will be appended to this element.
         *
         */
        this.domTarget = _get(this.config, 'domTarget', 'media-player');

        /**
         *
         * @type {Boolean}
         * @desc Specifies whether or not to display video controls (such as a play button, pause
         * button, volume control etc).
         *
         */
        this.controls = _get(this.config, 'controls', true);

        /**
         *
         * @type {Boolean}
         * @desc Instructs the video to start playing as soon as it can do so without stopping.
         * This results in the video starting automatically when the user loads the page. This
         * attribute is a boolean attribute. Therefore, the mere presence of this attribute equates
         * to a true value.
         *
         */
        this.autoPlay = _get(this.config, 'autoplay', true);

        /**
         *
         * @type {Boolean}
         * @desc Indicates the player that the video autoplay happened after a user initiated action.
         * For example, the user clicked a poster image and, after that, the video player was created.
         * In that case, playback should happen with sound, so the player is configured to be unmuted.
         *
         */
        this.clickToPlay = _get(this.config, 'clickToPlay', false);

        /**
         *
         * @type {Boolean}
         *
         */
        this.playsInline = _get(this.config, 'playsinline', true);

        /**
         *
         * @type {Boolean}
         * @desc This attribute is a CORS settings attribute. CORS stands
         * for Cross-Origin Resource Sharing. The purpose of the crossorigin attribute is to allow
         * you to configure the CORS requests for the element's fetched data. The values for the
         * crossorigin attribute are enumerated.
         *
         * Acceptable values:
         *
         * "anonymous": Cross-origin CORS requests for the element will not have the credentials
         * flag set. In other words, there will be no exchange of user credentials via cookies,
         * client-side SSL certificates or HTTP authentication.
         *
         * "use-credentials": Cross-origin CORS requests for the element will have the credentials
         * flag set.
         *
         * If this attribute is not specified, CORS is not used at all.
         *
         * An invalid keyword and an empty string will be handled as the anonymous value.
         *
         */
        this.crossOrigin = _get(this.config, 'crossorigin', 'anonymous');

        /**
         *
         * @type {Number}
         *
         */
        this.width = _get(this.config, 'width');

        /**
         *
         * @type {Number}
         *
         */
        this.height = _get(this.config, 'height');

        /**
         *
         * @type {Boolean}
         * @desc Specifies whether to keep re-playing the video once it has finished (i.e. it will
         * keep playing again and again until it is stopped).
         *
         */
        this.loop = _get(this.config, 'loop', false);

        /**
         *
         * @type {Boolean}
         * @desc Controls the default state of the video's audio output. If present, this attribute
         * results in the audio output being muted (i.e. there is no sound) upon loading. This
         * attribute will override the users' preferences. The user can then choose to turn on the
         * sound if he/she so wishes.
         *
         */
        this.mute = _get(this.config, 'mute', false);

        /**
         *
         * @type {Boolean}
         * @desc Specifies whether the video should be preloaded or not, and if so, how
         * it should be preloaded. Preloading allows the video to be loaded before it needs to be
         * used (eg, before the user clicks the "play" button). The preload attribute allows the
         * author to provide a hint to the browser/user agent about what the author thinks will
         * lead to the best user experience. This attribute may be ignored in some instances. For
         * example, if the user has disabled preloading or if there are network connectivity issues.
         *
         * Possible values: "none", "metadata", and "auto"
         *
         * Note: that the autoplay attribute can override the preload attribute (since if the
         * media plays, it naturally has to buffer first, regardless of the hint given by the
         * preload attribute). Despite this, you can still provide both attributes.
         *
         */
        this.preload = _get(this.config, 'preload', 'metadata');

        /**
         *
         * @type {object}
         * @desc Device specific infomation to aid in player type creation logic. Info includes browser, browser version, os, form-factor.
         * Example deviceInfo object:
         * ```js
         *  "userAgent": "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_9_2) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/35.0.1916.153 Safari/537.36",
         *  "is": {
         *      "ios": false,
         *      "blackberry": false,
         *      "android": false,
         *      "windowsphone": false,
         *      "windows": false,
         *      "linux": false,
         *      "macintosh": true,
         *      "webkit": true,
         *      "gecko": false,
         *      "trident": false,
         *      "chrome": true,
         *      "firefox": false,
         *      "opera": false,
         *      "operamini": false,
         *      "msie": false,
         *      "silk": false,
         *      "safari": false,
         *      "tablet": false,
         *      "phone": false,
         *      "desktop": true
         *  },
         *  "platform": "macintosh",
         *  "platformVersion": "10.9.2",
         *  "browser": "chrome",
         *  "browserVersion": "35.0.1916",
         *  "formFactor": "desktop"
         * ```
         *
         */
        this.deviceInfo = _get(this.config, 'deviceInfo', defaultPlayerConfig.deviceInfo);

        /**
         *
         * @type {MediaPlayer.HLSJSPlayerConfig}
         * @desc Configuration object to be passed to the hls.js constructor
         * Reference: https://github.com/video-dev/hls.js/blob/master/docs/API.md#fine-tuning
         *
         */
        this.hlsjsPlayerConfig = undefined;

    }

    /**
     *
     * @desc mediaItem getting
     * @returns {MediaItem} mediaItem
     *
     */
    get mediaItem() {

        if (!this._mediaItem instanceof MediaItem) {

            this._mediaItem = new MediaItem(this._mediaItem);

        }

        return this._mediaItem;

    }

    /**
     *
     * @desc mediaItem setter
     * @prop {MediaItem|Object} mediaItem The mediaItem to set. Can be an object or a MediaItem
     *
     */
    set mediaItem(value) {

        if (!value instanceof MediaItem) {

            this._mediaItem = new MediaItem(value);

        } else {

            this._mediaItem = new MediaItem(value);

        }

    }

}

export default PlayerTypeConfig;
